import { expect, is, test } from "@benchristel/taste"
import { render } from "preact"
import { useLayoutEffect, useState } from "preact/hooks"
import { promisesToClear, spy, uiTest } from "./testUtilities"

type Model = {
  subscriber: () => unknown
  readonly destroy: () => void
}

let nonce: number = 0

export function useModel<T extends Model>(factory: () => T): T {
  const [_, setNonce] = useState(0)
  const [model] = useState(factory)
  model.subscriber = () => setNonce(++nonce)

  useLayoutEffect(() => model.destroy, [model])

  return model
}

const noop = () => {}

test("useModel", {
  "returns the initial state": uiTest(async (el) => {
    let model
    const myModel = {
      subscriber: noop,
      destroy: noop,
    }
    function TestHarness() {
      model = useModel(() => myModel)
      return null
    }
    render(<TestHarness />, el)
    expect(model, is, myModel)
  }),

  "rerenders when the model changes": uiTest(async (el) => {
    const myModel = {
      subscriber: noop,
      destroy: noop,
      text: "Today!",
      update() {
        this.text = "Tomorrow!"
        this.subscriber()
      },
    }

    function TestHarness() {
      const model = useModel(() => myModel)
      return <p>{model.text}</p>
    }
    render(<TestHarness />, el)
    expect(el.innerText, is, "Today!")
    myModel.update()
    await promisesToClear()
    expect(el.innerText, is, "Tomorrow!")
  }),

  "destroys the model when the component unmounts": uiTest(async (el) => {
    const myModel = {
      subscriber: noop,
      destroy: spy(),
      text: "Today!",
      update: noop,
    }

    function TestHarness() {
      useModel(() => myModel)
      return null
    }
    render(<TestHarness />, el)
    expect(myModel.destroy.calls.length, is, 0)
    render(null, el)

    expect(myModel.destroy.calls.length, is, 1)
  }),
})
